// JobTable (round 2): one sortable table of jobs, which a StatusBar filters (BL-158 11), with what the
// board's lanes can't show: job, received, priority, job type, arrival window (and the booked time when
// it differs), technician, customer, area, address, tags, total; for Needs a look, why, in red; for
// Paused, why and since when. Every cell is one line with "…". Pressing a row opens it; in phase B its
// rows drag into a lane. Sort by any column: press its head; press again to turn the order round. One
// of several tables (Paused, by reason) carries its title over it. At phone widths every row is a touch
// target, 44 tall. Wider than its box (a phone), the table scrolls sideways inside it: the box then takes
// the keyboard's focus, and the edge it goes on past fades out (QA-841). Reads only --jobtable-*.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Cell, Column, Row, Table, TableBody, TableHeader, type SortDescriptor } from 'react-aria-components';
import { Glyph } from '../Glyph/Glyph.tsx';
import type { Pen } from '../pen.ts';
import { PenKey } from '../PenKey/PenKey.tsx';
import { watchScrollEdges } from '../scroll-edges.ts';
import './JobTable.css';

export interface JobRow {
  id: string;
  /** "#24611". */
  job: string;
  /** When it came in, in words ("Thu 22 Oct"), and as a number to sort by. */
  received: string;
  receivedAt: number;
  priority: 'Emergency' | 'High' | 'Normal' | 'Low';
  type: string;
  /** The arrival window promised ("10–12 p.m."), and the booked time when it differs. */
  window: string;
  booked?: string | null;
  /** Minutes from midnight, to sort by; null when unscheduled. */
  windowAt: number | null;
  tech?: { name: string; pen?: Pen | null } | null;
  customer: string;
  area: string;
  address: string;
  tags: string[];
  /** In cents. */
  total: number;
  /** For Needs a look: why. */
  why?: string | null;
  /** For Paused: why and since when ("Needs parts, since 9:40 a.m."). */
  paused?: string | null;
}

export type JobColumn =
  | 'job'
  | 'received'
  | 'priority'
  | 'type'
  | 'window'
  | 'tech'
  | 'customer'
  | 'area'
  | 'address'
  | 'tags'
  | 'total'
  | 'why'
  | 'paused';

const HEADS: Record<JobColumn, string> = {
  job: 'Job',
  received: 'Received',
  priority: 'Priority',
  type: 'Job type',
  window: 'Arrival window',
  tech: 'Technician',
  customer: 'Customer',
  area: 'Area',
  address: 'Address',
  tags: 'Tags',
  total: 'Total',
  why: 'Why',
  paused: 'Paused',
};

const PRIORITY = { Emergency: 0, High: 1, Normal: 2, Low: 3 } as const;

const sortValue = (r: JobRow, c: JobColumn): string | number => {
  switch (c) {
    case 'received':
      return r.receivedAt;
    case 'priority':
      return PRIORITY[r.priority];
    case 'window':
      return r.windowAt ?? Number.MAX_SAFE_INTEGER;
    case 'tech':
      return r.tech?.name ?? '￿';
    case 'tags':
      return r.tags.join(', ');
    case 'total':
      return r.total;
    case 'why':
      return r.why ?? '';
    case 'paused':
      return r.paused ?? '';
    default:
      return r[c];
  }
};

export const money = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const DEFAULT_COLUMNS: JobColumn[] = [
  'job',
  'window',
  'tech',
  'customer',
  'area',
  'type',
  'priority',
  'tags',
  'received',
  'total',
];

export interface JobTableProps {
  /** For screen readers: "Visits waiting for someone". */
  label: string;
  rows: JobRow[];
  columns?: JobColumn[];
  /** The first sort; then the viewer's. */
  sort?: SortDescriptor;
  onOpen?: (id: string) => void;
  /** When there are none, in words ("No visits need a look"). */
  empty?: string;
  /** A title over it, as a heading, when it is one of several ("Needs parts (2)"). */
  title?: string;
}

function CellFace({ r, c }: { r: JobRow; c: JobColumn }) {
  switch (c) {
    case 'job':
      return <span data-strong="">{r.job}</span>;
    case 'window':
      return r.booked ? (
        <>
          {r.window} <span data-muted="">(booked {r.booked})</span>
        </>
      ) : (
        <>{r.window}</>
      );
    case 'tech':
      return r.tech ? <PenKey pen={r.tech.pen}>{r.tech.name}</PenKey> : <span data-bad="">No one yet</span>;
    case 'priority':
      return r.priority === 'Emergency' ? <span data-bad="">{r.priority}</span> : <>{r.priority}</>;
    case 'tags':
      return r.tags.length ? <>{r.tags.join(', ')}</> : <span data-muted="">None</span>;
    case 'total':
      return <>{money(r.total)}</>;
    case 'why':
      return r.why ? <span data-bad="">{r.why}</span> : null;
    case 'paused':
      return r.paused ? <>{r.paused}</> : null;
    default:
      return <>{r[c]}</>;
  }
}

export function JobTable({
  label,
  rows,
  columns = DEFAULT_COLUMNS,
  sort,
  onOpen,
  empty = 'Nothing here',
  title,
}: JobTableProps) {
  const [by, setBy] = useState<SortDescriptor>(sort ?? { column: columns[0]!, direction: 'ascending' });
  const sorted = useMemo(() => {
    const c = by.column as JobColumn;
    const out = [...rows].sort((a, b) => {
      const x = sortValue(a, c);
      const y = sortValue(b, c);
      return typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
    });
    return by.direction === 'descending' ? out.reverse() : out;
  }, [rows, by]);
  const box = useRef<HTMLDivElement>(null);
  const [scrolls, setScrolls] = useState(false);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const stop = watchScrollEdges(el);
    const ro =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(() => setScrolls(el.scrollWidth > el.clientWidth + 1))
        : null;
    ro?.observe(el);
    if (el.firstElementChild) ro?.observe(el.firstElementChild);
    return () => {
      stop();
      ro?.disconnect();
    };
  }, []);
  return (
    <div className="fl-jobtable">
      {title ? <h2 className="fl-jobtable__title">{title}</h2> : null}
      <div
        ref={box}
        className="fl-jobtable__scroll"
        data-scrolls=""
        role={scrolls ? 'region' : undefined}
        aria-label={scrolls ? label : undefined}
        tabIndex={scrolls ? 0 : undefined}
      >
        <Table
          aria-label={label}
          className="fl-jobtable__table"
          sortDescriptor={by}
          onSortChange={setBy}
          onRowAction={(key) => onOpen?.(String(key))}
        >
          <TableHeader className="fl-jobtable__head">
            {columns.map((c, i) => (
              <Column
                key={c}
                id={c}
                isRowHeader={i === 0}
                allowsSorting
                className="fl-jobtable__th"
                data-figure={c === 'total' ? '' : undefined}
              >
                {({ sortDirection }) => (
                  <span className="fl-jobtable__sort">
                    {HEADS[c]}
                    {sortDirection ? (
                      <Glyph
                        name="chevron-right"
                        className={`fl-jobtable__glyph fl-jobtable__glyph--${sortDirection === 'ascending' ? 'up' : 'down'}`}
                      />
                    ) : null}
                  </span>
                )}
              </Column>
            ))}
          </TableHeader>
          <TableBody items={sorted} renderEmptyState={() => <p className="fl-jobtable__empty">{empty}</p>}>
            {(r) => (
              <Row id={r.id} className="fl-jobtable__row" textValue={`${r.job}, ${r.customer}`}>
                {columns.map((c) => (
                  <Cell
                    key={c}
                    className="fl-jobtable__td"
                    data-figure={c === 'total' ? '' : undefined}
                    data-wide={c === 'why' || c === 'paused' || c === 'address' ? '' : undefined}
                  >
                    <span className="fl-jobtable__cell">
                      <CellFace r={r} c={c} />
                    </span>
                  </Cell>
                ))}
              </Row>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
